import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { clamp } from "@perceptron/utils/math";
import { cn } from "@perceptron/utils/cn";

import type { TimelinePlayerBridge } from "../bridge";

/** The thumb stays grabbable when zoomed far into a long video. */
const MIN_THUMB_PX = 16;

type Props = {
  bridge: TimelinePlayerBridge;
  pixelsPerSecond: number;
  scrollLeft: number;
  viewportWidth: number;
  maxScroll: number;
  setScrollLeft: (value: number) => void;
};

/**
 * A slim strip along the top of the ruler while zoomed in: the whole video,
 * the visible window as a thumb to drag, and a playhead tick. Pressing the
 * track centres the window there. Wheel, keys and scrubbing stay the primary
 * ways to move, so it is hidden from assistive technology.
 */
export const TimelineOverview = React.memo(
  ({ bridge, pixelsPerSecond, scrollLeft, viewportWidth, maxScroll, setScrollLeft }: Props) => {
    const stripLeftRef = useRef(0);
    const playheadRef = useRef<HTMLDivElement>(null);
    const grabOffsetRef = useRef<number | null>(null);
    const [dragging, setDragging] = useState(false);

    // The visible window spans [scrollLeft, scrollLeft + viewportWidth] of a
    // scrollable width of maxScroll + viewportWidth; the strip shows it all.
    const scale = viewportWidth / (maxScroll + viewportWidth);
    const thumbWidth = Math.min(viewportWidth, Math.max(MIN_THUMB_PX, viewportWidth * scale));
    const thumbLeft = clamp((scrollLeft + viewportWidth / 2) * scale - thumbWidth / 2, 0, viewportWidth - thumbWidth);

    // The time subscription outlives renders, so it reads the current mapping from a ref.
    const pxPerSecondRef = useRef(pixelsPerSecond * scale);
    pxPerSecondRef.current = pixelsPerSecond * scale;
    const placeTick = (time: number) => {
      const tick = playheadRef.current;
      if (tick) tick.style.transform = `translateX(${Math.round(time * pxPerSecondRef.current)}px)`;
    };
    useEffect(() => bridge.subscribeToTime(placeTick), [bridge]);
    // A new zoom moves the tick in the same frame, without waiting for a time update.
    useLayoutEffect(() => placeTick(bridge.getCurrentTime()), [bridge, pixelsPerSecond, scale]);

    if (!(maxScroll > 0) || !(viewportWidth > 1)) return null;

    const scrollToThumbLeft = (left: number) =>
      setScrollLeft(clamp((left + thumbWidth / 2) / scale - viewportWidth / 2, 0, maxScroll));
    const pointerX = (event: React.PointerEvent) => event.clientX - stripLeftRef.current;

    return (
      <div
        aria-hidden
        className="group absolute left-0 top-0 z-20 cursor-grab touch-none"
        data-timeline-overview
        // A shallow band, so the rest of the ruler stays a scrub target.
        style={{ width: viewportWidth, height: 6 }}
        onPointerCancel={() => {
          grabOffsetRef.current = null;
          setDragging(false);
        }}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          event.stopPropagation();
          event.currentTarget.setPointerCapture(event.pointerId);
          stripLeftRef.current = event.currentTarget.getBoundingClientRect().left;
          const x = pointerX(event);
          const onThumb = x >= thumbLeft && x <= thumbLeft + thumbWidth;
          grabOffsetRef.current = onThumb ? x - thumbLeft : thumbWidth / 2;
          if (!onThumb) scrollToThumbLeft(x - thumbWidth / 2);
          setDragging(true);
        }}
        onPointerMove={(event) => {
          if (grabOffsetRef.current === null) return;
          scrollToThumbLeft(pointerX(event) - grabOffsetRef.current);
        }}
        onPointerUp={() => {
          grabOffsetRef.current = null;
          setDragging(false);
        }}
      >
        <div className="absolute inset-x-0 top-0 h-[2px] rounded-full bg-white/10" />
        <div
          className={cn(
            "absolute top-0 h-[2px] rounded-full transition-colors",
            dragging ? "bg-white/80" : "bg-white/40 group-hover:bg-white/60",
          )}
          style={{ left: thumbLeft, width: thumbWidth }}
        />
        <div className="absolute left-0 top-0 h-1 w-px bg-white" ref={playheadRef} />
      </div>
    );
  },
);

TimelineOverview.displayName = "TimelineOverview";
